import {Button, Modal} from 'antd';
import {Auth} from 'aws-amplify';
import _, {set} from 'lodash';
import moment from 'moment-timezone';
import React, {useEffect, useRef, useState} from 'react';
import {Dropdown} from 'react-bootstrap';
import {RiErrorWarningLine} from 'react-icons/ri';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {AcceptTC, AppLoader} from '..';
import useDetectOutside from '../../hooks/useDetectOutside';
import {
  changeSessionPage,
  clearAppointmentBookingDetails,
  clearClassBookingDetails,
  clearPublicUser,
  handleBranchChange,
  handleConnectToBranch,
  logoutUser,
  updateCarCount,
  updateCompanyMemberTnc,
} from '../../redux/actions/index';
import {
  IMAGE_URL,
  PRIMARY_COLOR,
  BASE_URL_WEB,
  CLIENT_SUPPORT,
  INVOICES_ENABLED,
} from '../../utility/constants';
import {AxiosHelperPortal, getInitials} from '../../utility/helperFunctions';
import {getClientInvoices, INVOICE_UPDATED_EVENT} from '../../services/invoiceService';
import {
  markMembershipPaymentLoginCheckCompleted,
  membershipPaymentLoginCheckCompleted,
  membershipPaymentRecoveryUrl,
} from '../../utility/membershipPaymentRecovery';
import {defaultClientBranch, sortedClientBranches} from '../../utility/clientLocation';
import './Header.css';
import {apiPostAcceptTnc} from './utils';

const Header = ({onlyLogout = false}) => {
  const initialRender = useRef(true);
  const paymentAttentionChecks = useRef(new Set());
  const appStoreUrl = 'https://apps.apple.com/us/app/coachfirst/id1524519609';
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.coachfirst.client';
  const deepLinkUrl = 'coachfirst://';
  const dispatch = useDispatch();
  const location = useLocation();
  const authDetails = useSelector(state => state.auth);
  const booking = useSelector(state => state.booking);
  const publicUser = useSelector(state => state.publicUser);
  const history = useHistory();
  const [activeCompany, setActiveCompany] = useState({});
  const [activeBranch, setActiveBranch] = useState({});
  const [companyChangeLoading, setCompanyChangeLoading] = useState(false);
  const [showAppPrompt, setShowAppPrompt] = useState(false);
  const [appPromptDismissed, setAppPromptDismissed] = useState(false);
  const [showAppStoreOptions, setShowAppStoreOptions] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [outstandingInvoiceCount, setOutstandingInvoiceCount] = useState(0);
  const activeCompanyBranches = sortedClientBranches(activeCompany);
  const [tnc, setTnc] = useState({
    tnc18Plus: false,
    tncPrivacy: false,
    tncTerms: false,
    isShowModal: false,
    isLoading: false,
  });

  /**
   * On login / member load: update car count up to 5.
   * This is the "first 5 logins" tracking.
   */
  useEffect(() => {
    const updateCarCountOnLogin = async () => {
      try {
        const currentCar = authDetails.companyData?.member?.car ?? 0;
        // Only increment while count is less than 5
        if (currentCar < 5 && authDetails?.companyData?.member?.mrf !== false) {
          const response = await AxiosHelperPortal(BASE_URL_WEB + 'update-car-count?', 'post');
          console.log('response for car count', response?.data?.carCount);
          dispatch(updateCarCount(response?.data?.carCount || currentCar + 1));
        }
      } catch (error) {
        console.error('Error updating car count', error);
      }
    };

    if (authDetails?.companyData?.member?.id) {
      updateCarCountOnLogin();
    }
  }, [authDetails?.companyData?.member?.id, dispatch]);

  useEffect(() => {
    if (!INVOICES_ENABLED) return undefined;

    const companyId = authDetails?.companyData?.id || authDetails?.companyData?.companyId;
    if (!companyId) {
      setOutstandingInvoiceCount(0);
      return undefined;
    }

    let cancelled = false;
    const refreshOutstandingInvoices = async () => {
      try {
        const response = await getClientInvoices(companyId);
        if (!cancelled) {
          setOutstandingInvoiceCount(Number(response?.data?.outstandingCount) || 0);
        }
      } catch (error) {
        if (!cancelled) setOutstandingInvoiceCount(0);
        console.error('Unable to load outstanding invoice count', error);
      }
    };

    refreshOutstandingInvoices();
    window.addEventListener(INVOICE_UPDATED_EVENT, refreshOutstandingInvoices);

    return () => {
      cancelled = true;
      window.removeEventListener(INVOICE_UPDATED_EVENT, refreshOutstandingInvoices);
    };
  }, [authDetails?.companyData?.companyId, authDetails?.companyData?.id]);

  useEffect(() => {
    const memberId = authDetails?.companyData?.member?.id;
    const companyId = authDetails?.companyData?.id || authDetails?.companyData?.companyId;
    if (!memberId || !companyId) return undefined;
    if (membershipPaymentLoginCheckCompleted()) return undefined;

    const checkKey = `${memberId}:${companyId}`;
    if (paymentAttentionChecks.current.has(checkKey)) return undefined;
    paymentAttentionChecks.current.add(checkKey);

    let cancelled = false;

    const redirectForMissedMembershipPayment = async () => {
      try {
        const params = new URLSearchParams({companyId});
        const response = await AxiosHelperPortal(
          `${BASE_URL_WEB}membership-v2/payment-attention?${params.toString()}`,
          'get'
        );
        const recoveryUrl = membershipPaymentRecoveryUrl(response?.data?.paymentAttention);
        const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

        if (cancelled) return;

        markMembershipPaymentLoginCheckCompleted();
        if (recoveryUrl && recoveryUrl !== currentUrl) {
          history.replace(recoveryUrl);
        }
      } catch (error) {
        console.error('Unable to check membership payment status', error);
      }
    };

    redirectForMissedMembershipPayment();

    return () => {
      cancelled = true;
    };
  }, [
    authDetails?.companyData?.companyId,
    authDetails?.companyData?.id,
    authDetails?.companyData?.member?.id,
    history,
  ]);

  useEffect(() => {
    document.body.setAttribute('class', 'sidebar-enable vertical-collpsed');
    const activeCompanyData =
      authDetails.companyData?.member_companies?.filter(
        item => item?.id === authDetails.companyData?.id
      ) || [];
    const activeBranchData =
      activeCompanyData[0]?.branches?.filter(
        item => item?.id === authDetails.companyData?.branch?.id
      ) || [];

    setActiveCompany(activeCompanyData[0]);
    setActiveBranch(activeBranchData[0]);

    if (initialRender.current) {
      initialRender.current = false;
    } else {
      if (
        booking.service &&
        booking.trainer &&
        booking.session_type === 'PUBLIC_APPOINTMENT_BOOKING' &&
        publicUser?.companyId &&
        publicUser.branchSelected
      ) {
        if (authDetails?.companyData?.branch?.id === publicUser?.branchSelected?.id) {
          dispatch(clearPublicUser());
          return;
        }
        const branch = authDetails?.companyData?.member_branches?.filter(
          item => item?.memberBranchesBranchId === publicUser?.branchSelected?.id
        );

        if (branch?.length) {
          handleBranch({id: publicUser.companyId}, publicUser?.branchSelected, true);
        } else
          dispatch(
            handleConnectToBranch({
              username: authDetails?.authDetails?.username,
              branchId: publicUser?.branchSelected?.id,
            })
          );
        dispatch(clearPublicUser());
      }
    }
  }, [
    authDetails.companyData,
    booking.service,
    booking.trainer,
    booking.session_type,
    dispatch,
    publicUser,
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent?.toLowerCase?.() || '';
      setIsMobileDevice(/iphone|ipad|ipod|android/.test(userAgent));
    }
  }, []);

  /**
   * Banner visibility logic:
   * - Shows ONLY while member.car < 5 (first five logins)
   * - Respects waiver logic
   * - Respects dismiss
   * - Only on '/', '/dashboard', '/class-booking'
   */
  useEffect(() => {
    const carCount = authDetails.companyData?.member?.car ?? 0;
    const hasSignedWaiver = authDetails.companyData?.waiver_signed;
    const hasWaiver = authDetails.companyData?.waiver;
    const isOnTargetPage =
      location.pathname === '/' ||
      location.pathname === '/dashboard' ||
      location.pathname === '/class-booking';

    console.log(
      '___showappprompt__',
      carCount,
      appPromptDismissed,
      isOnTargetPage,
      hasWaiver,
      hasSignedWaiver
    );

    // Stop showing the banner once user has logged in / seen it 5 times
    if (carCount >= 5 || appPromptDismissed) {
      setShowAppPrompt(false);
      setShowAppStoreOptions(false);
      return;
    }

    // Only show on selected pages and once waiver conditions are met
    if (isOnTargetPage && (!hasWaiver || hasSignedWaiver)) {
      setShowAppPrompt(true);
    } else {
      setShowAppPrompt(false);
    }
  }, [
    appPromptDismissed,
    authDetails.companyData?.member?.car,
    authDetails.companyData?.waiver_signed,
    authDetails.companyData?.waiver,
    location.pathname,
  ]);

  useEffect(() => {
    if (!showAppPrompt) {
      setShowAppStoreOptions(false);
    }
  }, [showAppPrompt]);

  const profileDropRef = useRef(null);
  const [showProfileDropdown, setShowProfileDropDown] = useDetectOutside(profileDropRef, false);

  const handleBranch = async (company, branch = null, publicBooking = null) => {
    console.log('___location__', location);
    if (location.pathname === '/booking' && !publicBooking) {
      dispatch(changeSessionPage(null));
    }
    setCompanyChangeLoading(true);
    const branchToActivate = defaultClientBranch(company, branch);
    const payload = {
      companyId: company?.id,
      branchId: branchToActivate?.id,
      username: authDetails?.authDetails?.username,
    };
    dispatch(handleBranchChange(payload));

    setCompanyChangeLoading(false);
  };

  const handleDismissAppPrompt = async () => {
    // Jump count by 5 so we never show again for this user
    const response = await AxiosHelperPortal(BASE_URL_WEB + 'update-car-count?', 'post', {
      increment: 5,
    });
    dispatch(updateCarCount(response?.data?.carCount || 5));
    setAppPromptDismissed(true);
    setShowAppPrompt(false);
    setShowAppStoreOptions(false);
  };

  const handleAppDownloadClick = () => {
    if (isMobileDevice) {
      window.location.href = deepLinkUrl;
      setTimeout(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(userAgent);
        const storeUrl = isIOS ? appStoreUrl : playStoreUrl;

        // Redirect to the appropriate store if the app doesn't open
        window.location.href = storeUrl;
      }, 1000);
      AxiosHelperPortal(BASE_URL_WEB + 'update-car-count?', 'post');
      setAppPromptDismissed(true);
      setShowAppPrompt(false);
      setShowAppStoreOptions(false);
      return;
    }

    setShowAppStoreOptions(p => !p);
  };

  const handleStoreLinkClick = storeUrl => {
    window.open(storeUrl, '_blank');
    AxiosHelperPortal(BASE_URL_WEB + 'update-car-count?', 'post');
    setAppPromptDismissed(true);
    setShowAppPrompt(false);
    setShowAppStoreOptions(false);
  };

  const handleCompanyChange = async company => {
    setActiveCompany(company);
    handleBranch(company);
  };

  const shouldShowCompanyLoader =
    companyChangeLoading ||
    (authDetails?.companyDataLoadingStatus && !authDetails?.companyData?.id);

  const setTncState = type => value => {
    setTnc(p => ({
      ...p,
      [type]: value,
    }));
  };

  const toggleTncModal = () => setTnc(p => ({...p, isShowModal: !p.isShowModal}));

  const _acceptTnc = async () => {
    setTnc(p => ({...p, isLoading: true}));
    const {status} = await apiPostAcceptTnc({timezone: moment.tz.guess()});
    if (status === 200) {
      dispatch(updateCompanyMemberTnc());
      toggleTncModal();
    } else {
      setTnc(p => ({...p, isLoading: false}));
      // show alert
      Modal.error({
        title: 'Error',
        content:
          'Something went wrong. Please try again. If the problem persists, please contact CoachFirst at info@coachfirst.com',
      });
    }
  };

  console.log('showAppPrompt', showAppPrompt);

  return (
    <>
      <header id="page-topbar">
        <div className="navbar-header position-relative">
          <div className="cf-logo pl-4 d-none d-sm-block">
            {/* logo & sidebar toggle removed for brevity */}
          </div>
          {shouldShowCompanyLoader ? (
            <AppLoader height={80} width={80} />
          ) : (
            <>
              {!onlyLogout && (
                <>
                  <div className="external-login-hdr company_logo top-50 start-50 ">
                    {authDetails?.companyData?.id && (
                      <Dropdown>
                        <div className="row">
                          <div className="col-12">
                            <div className="text-center">
                              <Link to="/" className="logo logo-light">
                                {authDetails.companyData?.imageUrl && (
                                  <span className="logo-sm mr-3">
                                    <img
                                      src={IMAGE_URL + authDetails.companyData?.imageUrl}
                                      alt=""
                                      className="img-fluid ex-logo"
                                      width="150"
                                    />
                                  </span>
                                )}
                                <span className="text-dark name">
                                  {authDetails.companyData?.name}
                                </span>
                              </Link>
                              <Dropdown.Toggle id="company_dropdown" className="toggle">
                                <i
                                  className="fas fa-chevron-down user-dropdown"
                                  aria-hidden="true"
                                ></i>
                              </Dropdown.Toggle>
                            </div>
                          </div>
                        </div>

                        <Dropdown.Menu className="company dropdown-menu show py-4">
                          {authDetails?.companyData?.member_companies?.map(item => (
                            <Dropdown.Item
                              className={`dropdown-item border-bottom p-4 px-3 ${
                                item?.branches?.length !== 1 && 'mult-address'
                              }`}
                              key={item?.id}
                              onClick={() => handleCompanyChange(item)}
                              active={activeCompany?.id === item?.id}
                            >
                              {item?.imageUrl && (
                                <img
                                  src={IMAGE_URL + item?.imageUrl}
                                  alt=""
                                  className="img-fluid ex-logo drop-item-img mr-3"
                                  width="60"
                                />
                              )}

                              <address className="d-inline-block mb-0">
                                <strong>{item?.name}</strong>
                                {item?.branches?.length === 1 && (
                                  <div>
                                    <i className="fa fa-map-marker mr-2" aria-hidden="true"></i>
                                    {item?.branches[0]?.street}, {item?.branches[0]?.state}, <br />
                                    {item?.branches[0]?.city}, {item?.branches[0]?.zip}
                                  </div>
                                )}
                              </address>
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>

                  {activeCompanyBranches.length >= 1 && (
                    <div className="external-login-hdr second-dropdown company_logo  top-50 start-50">
                      <Dropdown>
                        <div className="col-12">
                          <div className="text-center">
                            <div className="d-inline-block">
                              <span className="text-dark text-truncate">{activeBranch?.name}</span>
                              <Dropdown.Toggle id="company_dropdown" className="toggle">
                                <i
                                  className="fas fa-chevron-down user-dropdown"
                                  aria-hidden="true"
                                ></i>
                              </Dropdown.Toggle>
                            </div>
                          </div>
                        </div>

                        <Dropdown.Menu className="company dropdown-menu show py-4">
                          {activeCompanyBranches.map((item, index) => (
                            <Dropdown.Item
                              className="dropdown-item border-bottom p-4"
                              key={index}
                              onClick={() => handleBranch(activeCompany, item)}
                              active={activeBranch?.id === item?.id}
                            >
                              <img
                                src={IMAGE_URL + item?.imageUrl}
                                alt=""
                                className="img-fluid ex-logo"
                                width="40"
                              />
                              <span className="ml-3">{item?.name}</span>
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          <div className="d-flex">
            <div className="dropdown d-inline-block ms-2">
              {/* Search / chat button block omitted for brevity */}
            </div>

            <div className="dropdown d-inline-block mm">
              <button
                ref={profileDropRef}
                type="button"
                className={
                  showProfileDropdown
                    ? 'btn header-item waves-effect show'
                    : 'btn header-item waves-effect'
                }
                id="page-header-user-dropdown"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                onClick={() => setShowProfileDropDown(!showProfileDropdown)}
              >
                {authDetails.companyData?.member?.imageUrl ? (
                  <img
                    className="rounded-circle header-profile-user"
                    src={IMAGE_URL + authDetails.companyData?.member?.imageUrl}
                    alt=""
                  />
                ) : (
                  <span className="avName">
                    <span className="proName">
                      {getInitials(
                        authDetails.companyData?.member?.firstName,
                        authDetails.companyData?.member?.lastName
                      )}
                    </span>
                  </span>
                )}
                <span className="d-none d-xl-inline-block ms-1" key="t-henry">
                  {authDetails.companyData?.member?.firstName}{' '}
                  {authDetails.companyData?.member?.lastName}
                </span>
                <i className="fas fa-chevron-down d-none d-xl-inline-block user-dropdown"></i>
              </button>
              <div
                className={
                  showProfileDropdown
                    ? 'dropdown-menu dropdown-menu-end show'
                    : 'dropdown-menu dropdown-menu-end'
                }
              >
                {!onlyLogout ? (
                  <>
                    <Link className="dropdown-item" to="/class-booking">
                      <i className="bx bx-calendar font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Book Now/Calendar</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/">
                      <i className="bx bx-calendar font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Dashboard</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/family-members">
                      <i className="bx bx-user font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Members</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/credits">
                      <i className="bx bx-credit-card font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Credits</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/memberships">
                      <i className="bx bx-id-card font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Memberships</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    {INVOICES_ENABLED ? (
                      <>
                        <Link
                          className="dropdown-item cf-header__invoice-link"
                          to={`/invoices?companyId=${encodeURIComponent(
                            authDetails?.companyData?.id ||
                              authDetails?.companyData?.companyId ||
                              ''
                          )}`}
                        >
                          <span>
                            <i className="bx bx-receipt font-size-16 align-middle me-1"></i>{' '}
                            <span key="t-profile">Invoices</span>
                          </span>
                          {outstandingInvoiceCount > 0 ? (
                            <span
                              className="cf-header__invoice-badge"
                              aria-label={`${outstandingInvoiceCount} outstanding invoices`}
                            >
                              {outstandingInvoiceCount > 99 ? '99+' : outstandingInvoiceCount}
                            </span>
                          ) : null}
                        </Link>
                        <div className="dropdown-divider"></div>
                      </>
                    ) : null}
                    <Link className="dropdown-item" to="/orders">
                      <i className="bx bx-cart font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Orders</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/saved-cards">
                      <i className="bx bx-card font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Saved Cards</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/favorites">
                      <i className="bx bx-heart font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Favorites</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/reset-password">
                      <i className="bx bx-lock font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Reset Password</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/email-preferences">
                      <i className="bx bx-envelope font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-email-preferences">Email Preferences</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" to="/explore">
                      <i className="bx bx-user font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-profile">Explore</span>
                    </Link>
                    <div className="dropdown-divider"></div>
                    <a
                      className="dropdown-item"
                      href={CLIENT_SUPPORT}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bx bx-help-circle font-size-16 align-middle me-1"></i>{' '}
                      <span key="t-support">Client Support</span>
                    </a>
                    <div className="dropdown-divider"></div>
                  </>
                ) : null}

                <button
                  className="dropdown-item text-danger"
                  onClick={() => {
                    Auth.signOut()
                      .then(e => {
                        dispatch(logoutUser());
                        dispatch(clearPublicUser());
                        dispatch(clearAppointmentBookingDetails());
                        dispatch(clearClassBookingDetails());
                        localStorage.clear();
                        history.replace('/login');
                        console.log(e);
                      })
                      .catch(error => {
                        console.log(error);
                      });
                  }}
                >
                  <i className="bx bx-power-off font-size-16 align-middle me-1 text-danger"></i>{' '}
                  <span key="t-logout">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {authDetails?.companyData?.member && !_.has(authDetails.companyData?.member, 'toc') && (
        <div className="banner-container">
          <div className="row d-flex justify-content-center">
            <div className="col-sm-8 row my-4 justify-content-center d-flex">
              <div className="col d-flex">
                <p className="banner-text ml-4 font-size-16">
                  <RiErrorWarningLine size={25} color="white" /> To continue booking with your
                  coach, you must first accept CoachFirst’s Terms & Conditions and Privacy Policy
                </p>
              </div>
              <div className="col-auto d-flex">
                <Button danger style={{color: 'black'}} onClick={toggleTncModal}>
                  Click here
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Download Prompt for New Users (first 5 logins) */}
      {showAppPrompt && (
        <div className="banner-container">
          <div className="row d-flex justify-content-center">
            <div className="col-sm-9 row my-4 justify-content-center d-flex">
              <div className="col d-flex">
                <p className="banner-text ml-4 font-size-16">
                  <RiErrorWarningLine size={25} color="white" /> Welcome to CoachFirst! For the best
                  experience, download our mobile app.
                </p>
              </div>
              <div className="col-auto d-flex align-items-center">
                <Button
                  type="primary"
                  style={{backgroundColor: PRIMARY_COLOR, color: '#ffffff'}}
                  onClick={handleAppDownloadClick}
                >
                  Download App
                </Button>
                {!isMobileDevice && showAppStoreOptions && (
                  <div className="d-flex align-items-center" style={{marginLeft: '10px'}}>
                    <Button
                      style={{marginRight: '10px', color: 'black'}}
                      onClick={() => handleStoreLinkClick(appStoreUrl)}
                    >
                      iOS App
                    </Button>
                    <Button
                      style={{color: 'black'}}
                      onClick={() => handleStoreLinkClick(playStoreUrl)}
                    >
                      Android App
                    </Button>
                  </div>
                )}
                <Button
                  style={{marginLeft: '10px', color: 'black'}}
                  onClick={handleDismissAppPrompt}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Please check the items and click on accept"
        open={tnc.isShowModal}
        onCancel={toggleTncModal}
        centered
        footer={
          <Button
            danger
            type="primary"
            style={{
              backgroundColor: PRIMARY_COLOR,
              opacity: !tnc.tnc18Plus || !tnc.tncPrivacy || !tnc.tncTerms ? '0.5' : 1,
              color: '#ffffff',
            }}
            disabled={!tnc.tnc18Plus || !tnc.tncPrivacy || !tnc.tncTerms}
            onClick={_acceptTnc}
            loading={tnc.isLoading}
          >
            Accept
          </Button>
        }
      >
        <AcceptTC setDispatchItems={setTncState} state={tnc} />
      </Modal>
    </>
  );
};

export default Header;
